/**
 * Compress an image file in the browser before upload.
 *
 * - Scales down to maxWidth px while keeping aspect ratio.
 * - Re-encodes as JPEG at the given quality.
 * - Returns a base64 data URL.
 *
 * For chat we deliberately compress hard (1024 px @ 75% JPEG) — average
 * output is ~100-200 KB per photo, well under our 500 KB target so a user
 * on 3G can send one in ~1-2 seconds without crushing storage costs.
 */
export async function compressImage(file, { maxWidth = 1024, quality = 0.75 } = {}) {
  if (!file) throw new Error("No file");
  if (!file.type || !file.type.startsWith("image/")) throw new Error("Not an image");

  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("read failed"));
    r.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("decode failed"));
    i.src = dataUrl;
  });

  const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  // toDataURL is sync; toBlob is async — we use toBlob then re-read so we can
  // also offer the final size to callers if they want to gate on it.
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) throw new Error("encode failed");

  const out = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("read encoded failed"));
    r.readAsDataURL(blob);
  });
  return { dataUrl: out, width: w, height: h, bytes: blob.size };
}
